import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCms } from '../cms/CmsContext';
import { Eye, EyeOff, X } from 'lucide-react';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import SponsorsSection from '../components/SponsorsSection';
import ScrollToTop from '../components/ScrollToTop';
import TornPaperWrapper from '../components/TornPaperWrapper';
import './LoginPage.css';
import { getCmsImagePath } from '../cms/cmsData';
import { getArabicTranslation } from '../utils/arabicTranslations';
import { getApiErrorMessage } from '../services/api';

// Sanitize redirect param to prevent open redirect / XSS
const sanitizeRedirect = (redirect) => {
  if (!redirect || typeof redirect !== 'string') return '/dashboard';
  const trimmed = redirect.trim();
  // Only allow relative paths starting with a single /
  // Block: //, http:, https:, javascript:, data:, vbscript:, or any scheme
  if (
    trimmed.startsWith('//') ||
    /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed) ||
    !trimmed.startsWith('/')
  ) {
    return '/dashboard';
  }
  return trimmed;
};

const OTP_DIGITS = 6;

const isAdminLoginUser = (user) => {
  const role = String(user?.role || '').trim().toLowerCase();
  return Boolean(
    user?.is_staff
    || user?.is_superuser
    || user?.is_admin
    || ['admin', 'superadmin', 'staff', 'staff_scanner'].includes(role)
  );
};

const getLoginDestination = (user, redirect) => {
  const safeRedirect = redirect && redirect.trim() ? sanitizeRedirect(redirect) : '';
  const role = String(user?.role || '').trim().toLowerCase();
  const isFestivalAdmin = Boolean(user?.is_superuser || user?.is_admin || ['admin', 'superadmin'].includes(role));
  if (isFestivalAdmin) return safeRedirect.startsWith('/hq') ? safeRedirect : '/hq/intelligence';
  if (isAdminLoginUser(user)) return safeRedirect.startsWith('/hq') ? safeRedirect : '/hq/overview';
  return safeRedirect || '/dashboard';
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, isAuthenticated, requestPhoneLoginCode, verifyPhoneLoginCode } = useAuth();
  const { content } = useCms();
  const cms = content?.loginPage || {};
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isPhoneLoginOpen, setIsPhoneLoginOpen] = useState(false);
  const [phoneStep, setPhoneStep] = useState('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneCode, setPhoneCode] = useState(Array(OTP_DIGITS).fill(''));
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneSuccess, setPhoneSuccess] = useState('');
  const [phoneDebugCode, setPhoneDebugCode] = useState('');
  const codeInputRefs = React.useRef([]);

  // Redirect if already logged in
  React.useEffect(() => {
    if (isAuthenticated) {
      const searchParams = new URLSearchParams(location.search);
      const redirect = searchParams.get('redirect');
      navigate(getLoginDestination(user, redirect));
    }
  }, [isAuthenticated, navigate, location, user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const resetPhoneLoginState = () => {
    setPhoneStep('phone');
    setPhoneNumber('');
    setPhoneCode(Array(OTP_DIGITS).fill(''));
    setPhoneLoading(false);
    setPhoneError('');
    setPhoneSuccess('');
    setPhoneDebugCode('');
  };

  const openPhoneLogin = () => {
    resetPhoneLoginState();
    setError('');
    setIsPhoneLoginOpen(true);
  };

  const closePhoneLogin = () => {
    setIsPhoneLoginOpen(false);
    resetPhoneLoginState();
  };

  const handlePhoneRequestCode = async (e) => {
    e.preventDefault();
    if (phoneLoading) return;
    if (!phoneNumber.trim()) {
      setPhoneError('Phone number is required.');
      return;
    }

    setPhoneLoading(true);
    setPhoneError('');
    setPhoneSuccess('');

    try {
      const result = await requestPhoneLoginCode(phoneNumber.trim());
      const normalizedPhone = result?.data?.phone || phoneNumber.trim();
      setPhoneNumber(normalizedPhone);
      setPhoneCode(Array(OTP_DIGITS).fill(''));
      setPhoneStep('code');
      setPhoneSuccess('Enter the 6-digit code we just sent to your phone.');
      setPhoneDebugCode(result?.data?.debug_code || '');
      setTimeout(() => {
        if (codeInputRefs.current[0]) {
          codeInputRefs.current[0].focus();
        }
      }, 0);
    } catch (err) {
      setPhoneError(err.message || 'Could not send SMS code. Please try a new number.');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handlePhoneCodeChange = (index, value) => {
    const nextValue = String(value || '').replace(/\D/g, '').slice(-1);
    const updated = [...phoneCode];
    updated[index] = nextValue;
    setPhoneCode(updated);
    setPhoneError('');

    if (nextValue && index < OTP_DIGITS - 1 && codeInputRefs.current[index + 1]) {
      codeInputRefs.current[index + 1].focus();
    }
  };

  const handlePhoneCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !phoneCode[index] && index > 0 && codeInputRefs.current[index - 1]) {
      codeInputRefs.current[index - 1].focus();
    }
  };

  const handlePhoneCodePaste = (e) => {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, OTP_DIGITS);
    if (!pasted) return;

    e.preventDefault();
    const updated = Array(OTP_DIGITS).fill('');
    pasted.split('').forEach((digit, idx) => {
      updated[idx] = digit;
    });
    setPhoneCode(updated);

    const focusIndex = Math.min(pasted.length, OTP_DIGITS - 1);
    if (codeInputRefs.current[focusIndex]) {
      codeInputRefs.current[focusIndex].focus();
    }
  };

  const handlePhoneVerifyCode = async (e) => {
    e.preventDefault();
    if (phoneLoading) return;

    const code = phoneCode.join('');
    if (code.length !== OTP_DIGITS) {
      setPhoneError('Please enter the full 6-digit code.');
      return;
    }

    setPhoneLoading(true);
    setPhoneError('');

    try {
      const result = await verifyPhoneLoginCode(phoneNumber, code);
      if (result?.success) {
        const searchParams = new URLSearchParams(location.search);
        const redirect = searchParams.get('redirect');
        navigate(getLoginDestination(result?.data?.user, redirect));
        closePhoneLogin();
      } else {
        setPhoneError(result?.error?.message || 'Login failed. Please try a new number.');
      }
    } catch (err) {
      setPhoneError(err.message || 'Invalid code. Please try a new number.');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleTryNewNumber = () => {
    setPhoneStep('phone');
    setPhoneCode(Array(OTP_DIGITS).fill(''));
    setPhoneError('');
    setPhoneSuccess('');
    setPhoneDebugCode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password);
      if (result?.success) {
        const searchParams = new URLSearchParams(location.search);
        const redirect = searchParams.get('redirect');
        navigate(getLoginDestination(result?.data?.user, redirect));
      } else {
        setError(getApiErrorMessage(result, 'Login failed'));
      }
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 0
          ? 'Unable to reach the local API. Make sure the backend is running on port 8000.'
          : (err.message || 'Login failed. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <AnnouncementBar />
      
      <section className="hero-section">
        <div className="hero-background-wrapper">
          <video src={"/hero-final.mp4"} alt="oc mena festival" className="hero-background-video" autoPlay muted loop playsInline />
          <div className="hero-gradient-overlay"></div>
        </div>

        <TornPaperWrapper>
          <h1 className="card-title" style={{ paddingBottom: '30px' }}>{cms.title} <span className="arabic-text on-white">{getArabicTranslation('Login')}</span></h1>
          
          <div className="auth-options" style={{ paddingBottom: '30px' }}>
            <Link to={`/login${location.search}`} className="auth-option active" title="Login">
              {cms.loginTab}
              <div className="active-indicator"></div>
            </Link>
            <Link to={`/signup${location.search}`} className="auth-option" title="Register">
              {cms.registerTab}
            </Link>
          </div>

          <div className="auth-form">
            {!isPhoneLoginOpen ? (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="email">{cms.emailLabel}</label>
                  <div className="email-input-wrapper">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder=""
                      autoComplete="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck="false"
                      required
                    />
                  </div>
                </div>

                <div className="form-group password-group">
                  <label htmlFor="password">{cms.passwordLabel}</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder=""
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="form-options-left">
                  <label className="remember-me">
                    <input type="checkbox" />
                    <span>{cms.rememberMe}</span>
                  </label>
                </div>

                {error && <div className="error-message" style={{color: '#e74c3c', marginBottom: '1rem', textAlign: 'center'}}>{error}</div>}

                <button type="submit" className="btn-primary submit-btn" disabled={loading}>
                  {loading ? cms.submittingButton : 'Log In'}
                </button>

                <button
                  type="button"
                  className="phone-login-link"
                  onClick={openPhoneLogin}
                >
                  Login with your phone number
                </button>

                <div className="forgot-password-left">
                  <Link to="/forgot-password" className="forgot-link-grey" title="Reset your password">Lost your password?</Link>
                </div>

                <div className="register-hint-left">
                  <span className="register-hint-text">Don&apos;t have an account? Register </span>
                  <Link to={`/signup${location.search}`} className="register-hint-link" title="Register for an account">here</Link>
                </div>
              </form>
            ) : (
              <div className="phone-login-inline-shell">
                <div className="phone-login-inline-header">
                  <button type="button" className="phone-login-cancel-btn" onClick={closePhoneLogin}>Back to email login</button>
                  <button type="button" className="phone-login-close-btn" onClick={closePhoneLogin} aria-label="Close phone login">
                    <X size={18} />
                  </button>
                </div>

                {phoneStep === 'phone' ? (
                  <form onSubmit={handlePhoneRequestCode} className="phone-login-form">
                    <h2 className="phone-login-title">Login with your phone number</h2>

                    <label htmlFor="phone-login-number" className="phone-login-label">Phone Number</label>
                    <input
                      type="tel"
                      id="phone-login-number"
                      className="phone-login-input"
                      value={phoneNumber}
                      onChange={(e) => {
                        setPhoneNumber(e.target.value);
                        setPhoneError('');
                      }}
                      placeholder="(000) 000-0000"
                      autoComplete="tel"
                      required
                    />

                    {phoneError && <div className="phone-login-error">{phoneError}</div>}

                    <button type="submit" className="phone-login-submit-btn" disabled={phoneLoading}>
                      {phoneLoading ? 'Sending...' : 'Login'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handlePhoneVerifyCode} className="phone-login-form">
                    <h2 className="phone-login-title">Enter 6-digit authentication code</h2>
                    <p className="phone-login-subtitle">Code sent to {phoneNumber}</p>

                    <div className="phone-code-grid" onPaste={handlePhoneCodePaste}>
                      {phoneCode.map((digit, idx) => (
                        <input
                          key={`otp-${idx}`}
                          ref={(el) => {
                            codeInputRefs.current[idx] = el;
                          }}
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={1}
                          className="phone-code-input"
                          value={digit}
                          onChange={(e) => handlePhoneCodeChange(idx, e.target.value)}
                          onKeyDown={(e) => handlePhoneCodeKeyDown(idx, e)}
                          aria-label={`Digit ${idx + 1}`}
                        />
                      ))}
                    </div>

                    {phoneSuccess && <div className="phone-login-success">{phoneSuccess}</div>}
                    {phoneDebugCode && <div className="phone-login-debug">Local fake code: {phoneDebugCode}</div>}
                    {phoneError && <div className="phone-login-error">{phoneError}</div>}

                    <button type="submit" className="phone-login-submit-btn" disabled={phoneLoading}>
                      {phoneLoading ? 'Verifying...' : 'Verify'}
                    </button>

                    <button type="button" className="phone-login-try-new-btn" onClick={handleTryNewNumber}>
                      Try a new number
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </TornPaperWrapper>

        <div className="lanterns-container">
          <img loading="lazy" src={getCmsImagePath("lanterns", "/lanterns.webp")} alt="festival lanterns" className="lanterns-image" />
        </div>
      </section>

      {/* Sponsors Section */}
      <SponsorsSection />

      {/* Footer */}
      <Footer />
      
      <ScrollToTop />

    </div>
  );
};

export default LoginPage;
